import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  List,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';

const AI_ASSISTANT_DIALOG_COPY = {
  de: {
    title: 'KI-Assistent: Parameter-Vorschläge',
    subtitle: 'Beschreiben Sie Ihre Studie und erhalten Sie KI-gestützte Parameter-Empfehlungen',
    studyDescription: 'Studienbeschreibung',
    studyDescriptionPlaceholder: "Beschreiben Sie Ihre Studie... z.B. 'Retrospektive Kohortenstudie zur Untersuchung von Sepsis-Biomarkern bei ICU-Patienten. Ziel ist die Identifikation prädiktiver Marker für Mortalität.'",
    studyType: 'Studientyp (optional)',
    specialty: 'Fachrichtung (optional)',
    notSpecified: 'Nicht angegeben',
    studyTypes: {
      retrospective: 'Retrospektiv',
      prospective: 'Prospektiv',
      rct: 'Randomisierte Studie',
      observational: 'Beobachtungsstudie',
    },
    specialties: {
      icu: 'Intensivmedizin',
      cardiology: 'Kardiologie',
      nephrology: 'Nephrologie',
      emergency: 'Notfallmedizin',
      internal: 'Innere Medizin',
    },
    suggestionsTitle: '{{count}} Parameter vorgeschlagen',
    suggestionsHint: 'Wählen Sie die Parameter aus, die als Filter angewendet werden sollen',
    confidence: '{{value}}% Konfidenz',
    newSearch: 'Neue Suche',
    selectedCount: '{{selected}} von {{total}} ausgewählt',
    cancel: 'Abbrechen',
    analyzing: 'Analysiere...',
    suggestParameters: 'Parameter vorschlagen',
    suggestParametersUnavailable: 'Diese Funktion ist noch nicht angebunden und schlägt keine Parameter vor.',
    applyAsFilters: 'Als Filter anwenden ({{count}})',
  },
  en: {
    title: 'AI assistant: parameter suggestions',
    subtitle: 'Describe your study and receive AI-assisted parameter recommendations',
    studyDescription: 'Study description',
    studyDescriptionPlaceholder: "Describe your study... e.g. 'Retrospective cohort study investigating sepsis biomarkers in ICU patients. The goal is to identify predictive markers for mortality.'",
    studyType: 'Study type (optional)',
    specialty: 'Specialty (optional)',
    notSpecified: 'Not specified',
    studyTypes: {
      retrospective: 'Retrospective',
      prospective: 'Prospective',
      rct: 'Randomized study',
      observational: 'Observational study',
    },
    specialties: {
      icu: 'Intensive care',
      cardiology: 'Cardiology',
      nephrology: 'Nephrology',
      emergency: 'Emergency medicine',
      internal: 'Internal medicine',
    },
    suggestionsTitle: '{{count}} parameters suggested',
    suggestionsHint: 'Select the parameters that should be applied as filters',
    confidence: '{{value}}% confidence',
    newSearch: 'New search',
    selectedCount: '{{selected}} of {{total}} selected',
    cancel: 'Cancel',
    analyzing: 'Analyzing...',
    suggestParameters: 'Suggest parameters',
    suggestParametersUnavailable: 'This feature is not connected yet and will not suggest any parameters.',
    applyAsFilters: 'Apply as filters ({{count}})',
  },
  pt: {
    title: 'Assistente de IA: sugestões de parâmetros',
    subtitle: 'Descreva seu estudo e receba recomendações de parâmetros com apoio de IA',
    studyDescription: 'Descrição do estudo',
    studyDescriptionPlaceholder: "Descreva seu estudo... por exemplo: 'Estudo de coorte retrospectivo para investigar biomarcadores de sepse em pacientes de UTI. O objetivo é identificar marcadores preditivos de mortalidade.'",
    studyType: 'Tipo de estudo (opcional)',
    specialty: 'Especialidade (opcional)',
    notSpecified: 'Não especificado',
    studyTypes: {
      retrospective: 'Retrospectivo',
      prospective: 'Prospectivo',
      rct: 'Estudo randomizado',
      observational: 'Estudo observacional',
    },
    specialties: {
      icu: 'Medicina intensiva',
      cardiology: 'Cardiologia',
      nephrology: 'Nefrologia',
      emergency: 'Medicina de emergência',
      internal: 'Medicina interna',
    },
    suggestionsTitle: '{{count}} parâmetros sugeridos',
    suggestionsHint: 'Selecione os parâmetros que devem ser aplicados como filtros',
    confidence: '{{value}}% de confiança',
    newSearch: 'Nova busca',
    selectedCount: '{{selected}} de {{total}} selecionados',
    cancel: 'Cancelar',
    analyzing: 'Analisando...',
    suggestParameters: 'Sugerir parâmetros',
    suggestParametersUnavailable: 'Esta função ainda não está conectada e não sugere parâmetros.',
    applyAsFilters: 'Aplicar como filtros ({{count}})',
  },
};

/**
 * AI Assistant Dialog Component
 * AI-based parameter suggestions for studies
 *
 * New workflow: User selects which suggestions to apply as filters (chips)
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {string} studyDescription - Study description input
 * @param {Function} setStudyDescription - Study description setter
 * @param {string} studyType - Study type input
 * @param {Function} setStudyType - Study type setter
 * @param {string} specialty - Specialty input
 * @param {Function} setSpecialty - Specialty setter
 * @param {Array} aiSuggestions - AI-generated suggestions
 * @param {Function} setAiSuggestions - AI suggestions setter
 * @param {Array} selectedSuggestions - Selected suggestion IDs
 * @param {Function} setSelectedSuggestions - Selected suggestions setter
 * @param {Function} handleGenerateSuggestions - Handler for generating suggestions
 * @param {Function} handleApplySuggestionsAsFilter - Handler for applying suggestions as filter
 */
const AIAssistantDialog = ({
  open,
  onClose,
  studyDescription,
  setStudyDescription,
  studyType,
  setStudyType,
  specialty,
  setSpecialty,
  aiSuggestions,
  setAiSuggestions,
  selectedSuggestions,
  setSelectedSuggestions,
  handleGenerateSuggestions,
  handleApplySuggestionsAsFilter,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = AI_ASSISTANT_DIALOG_COPY[language];

  // Toggle handler for individual suggestions
  const handleToggleSuggestion = (suggestionId) => {
    setSelectedSuggestions(prev =>
      prev.includes(suggestionId)
        ? prev.filter(id => id !== suggestionId)
        : [...prev, suggestionId]
    );
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {copy.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {copy.subtitle}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {aiSuggestions.length === 0 ? (
          // Input Phase
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label={copy.studyDescription}
              placeholder={copy.studyDescriptionPlaceholder}
              value={studyDescription}
              onChange={(e) => setStudyDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{copy.studyType}</InputLabel>
                  <Select
                    value={studyType}
                    onChange={(e) => setStudyType(e.target.value)}
                    label={copy.studyType}
                  >
                    <MenuItem value="">{copy.notSpecified}</MenuItem>
                    <MenuItem value="retrospective">{copy.studyTypes.retrospective}</MenuItem>
                    <MenuItem value="prospective">{copy.studyTypes.prospective}</MenuItem>
                    <MenuItem value="rct">{copy.studyTypes.rct}</MenuItem>
                    <MenuItem value="observational">{copy.studyTypes.observational}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{copy.specialty}</InputLabel>
                  <Select
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    label={copy.specialty}
                  >
                    <MenuItem value="">{copy.notSpecified}</MenuItem>
                    <MenuItem value="icu">{copy.specialties.icu}</MenuItem>
                    <MenuItem value="cardiology">{copy.specialties.cardiology}</MenuItem>
                    <MenuItem value="nephrology">{copy.specialties.nephrology}</MenuItem>
                    <MenuItem value="emergency">{copy.specialties.emergency}</MenuItem>
                    <MenuItem value="internal">{copy.specialties.internal}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Results Phase
          <Box sx={{ pt: 2 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                {copy.suggestionsTitle.replace('{{count}}', aiSuggestions.length)}
              </Typography>
              <Typography variant="caption">
                {copy.suggestionsHint}
              </Typography>
            </Alert>

            <List>
              {aiSuggestions.map((suggestion) => (
                <Paper
                  key={suggestion.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    border: selectedSuggestions.includes(suggestion.id) ? '2px solid' : '1px solid',
                    borderColor: selectedSuggestions.includes(suggestion.id) ? 'primary.main' : 'divider',
                  }}
                  onClick={() => handleToggleSuggestion(suggestion.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedSuggestions.includes(suggestion.id)}
                      onChange={() => handleToggleSuggestion(suggestion.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        }
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <AutoAwesomeIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {suggestion.name}
                        </Typography>
                        <Chip label={suggestion.category} size="small" />
                        {suggestion.omopMapped && (
                          <Chip label="OMOP" size="small" color="success" />
                        )}
                        {suggestion.confidence !== undefined && suggestion.confidence !== null && (
                          <Chip
                            label={copy.confidence.replace('{{value}}', suggestion.confidence)}
                            size="small"
                            color={suggestion.confidence >= 90 ? 'success' : 'default'}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {suggestion.reason}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAiSuggestions([]);
                  setSelectedSuggestions([]);
                }}
              >
                {copy.newSearch}
              </Button>
              <Typography variant="caption" color="textSecondary">
                {copy.selectedCount
                  .replace('{{selected}}', selectedSuggestions.length)
                  .replace('{{total}}', aiSuggestions.length)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{copy.cancel}</Button>
        {aiSuggestions.length === 0 ? (
          // Es gibt noch keine Schnittstelle, die aus der Studienbeschreibung
          // Parameter ableitet (#273) -- der Knopf bleibt deshalb sichtbar
          // deaktiviert (AK4), statt eine erfundene Antwort vorzutaeuschen.
          <Tooltip title={copy.suggestParametersUnavailable}>
            <span>
              <Button
                variant="contained"
                onClick={handleGenerateSuggestions}
                disabled
                startIcon={<AutoAwesomeIcon />}
              >
                {copy.suggestParameters}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            onClick={handleApplySuggestionsAsFilter}
            startIcon={<AutoAwesomeIcon />}
            disabled={selectedSuggestions.length === 0}
          >
            {copy.applyAsFilters.replace('{{count}}', selectedSuggestions.length)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIAssistantDialog;
